import { Kicker } from '@developerz/ui';
import { A } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import type { AgentActivity } from '../lib/agent-activity';
import { runningSummary, soonestReset, workingKeyCount } from '../lib/agents';
import type { KeyRow } from '../lib/keys';
import { orgHref } from '../lib/org';
import { absoluteWithZone, untilLabel, useMinuteClock } from '../lib/time';
import { AgentsStripFrame } from './AgentsFrames';

// The Agents page's status strip (#5626): ONE hairline row, two labelled groups —
// "AI keys: 6 ready · next one back in 4h 10m · Manage keys →" and "Running now: 3
// tasks · 2 agents" — each said in words, so an owner reads the state without hovering
// or decoding a dash. It replaces the old pool panel's three unlabelled stats.
//
// A read that has not landed says "checking…", a failed read says "couldn't check right
// now": never a guessed zero and never a dash, because a dash could be a healthy
// nothing. The countdown re-renders each minute off one clock so "4h 10m" does not
// sit stale on a page left open; its hover title carries the exact local time with a
// zone abbreviation, because "4h 10m" alone cannot say WHICH wall-clock minute.

export type AgentsStatusStripProps = {
  readonly keys: { readonly data: readonly KeyRow[] | undefined; readonly isError: boolean };
  readonly activity: { readonly data: AgentActivity | undefined; readonly isError: boolean };
  /** A fixed clock (epoch ms), for a test to pin the countdown. Unset, the strip reads
   * the real clock and re-reads it every minute. */
  readonly now?: number | undefined;
};

// Stands in for the duration while the reset sentence is split around it, so the
// countdown can sit in its own titled element wherever the locale puts it.
const DURATION_SLOT = '⁣';

export function AgentsStatusStrip(props: AgentsStatusStripProps): JSX.Element {
  const minute = useMinuteClock();
  const clock = (): number => props.now ?? minute();

  const ready = (): number | undefined =>
    props.keys.isError ? undefined : workingKeyCount(props.keys.data, clock());
  const reset = (): string | null =>
    props.keys.isError ? null : soonestReset(props.keys.data, clock());
  const keysValue = (): string =>
    props.keys.isError
      ? t('agents.pool.keys_unknown')
      : ready() === undefined
        ? t('agents.pool.keys_checking')
        : t('agents.pool.keys_ready', { n: ready() as number });
  const resetParts = (): readonly [string, string] => {
    const [before = '', after = ''] = t('agents.pool.reset', { duration: DURATION_SLOT }).split(
      DURATION_SLOT,
    );
    return [before, after];
  };

  const running = () =>
    props.activity.isError ? undefined : runningSummary(props.activity.data?.agents);
  const runningValue = (): string => {
    const r = running();
    if (r === undefined) {
      return props.activity.isError
        ? t('agents.pool.running_unknown')
        : t('agents.pool.running_checking');
    }
    const tasks = tPlural('agents.pool.running_tasks', r.tasks);
    const agents = tPlural('agents.pool.running_agents', r.agents);
    return `${tasks} · ${agents}`;
  };
  const idle = (): boolean => running()?.tasks === 0;

  return (
    <AgentsStripFrame
      keys={
        <>
          <Kicker as="span">{t('agents.pool.keys_label')}</Kicker>{' '}
          <span
            class="agents__strip-value"
            classList={{ 'agents__strip-value--warning': ready() === 0 }}
          >
            {keysValue()}
          </span>
          <Show when={reset()}>
            {(iso) => (
              <>
                {' · '}
                {resetParts()[0]}
                <span title={t('agents.pool.reset_title', { when: absoluteWithZone(iso()) })}>
                  {untilLabel(iso(), clock())}
                </span>
                {resetParts()[1]}
              </>
            )}
          </Show>
          {' · '}
          <A href={orgHref('/account/keys')}>{t('agents.pool.manage')}</A>
        </>
      }
      running={
        <Show
          when={!idle()}
          fallback={<span class="agents__strip-value">{t('agents.pool.running_none')}</span>}
        >
          <Kicker as="span">{t('agents.pool.running_label')}</Kicker>{' '}
          <span class="agents__strip-value">{runningValue()}</span>
        </Show>
      }
    />
  );
}
